<template>
  <BaseModal
    :title="t('spareParts.inquiry.title', { partName: part.name })"
    size="xl"
    @close="$emit('close')"
  >
    <!-- Part Info -->
    <div class="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <h4 class="font-bold text-gray-900 mb-2 text-lg">{{ part.name }}</h4>
      <p class="text-sm text-gray-600 font-medium">{{ translations.partNumber }}: {{ part.part_number }}</p>
      <p v-if="part.brand" class="text-sm text-gray-600 font-medium">
        {{ translations.brand }}: {{ part.brand.name }}
      </p>
      <p v-if="part.machine" class="text-sm text-gray-600 font-medium">
        {{ translations.compatibleWith }}: {{ part.machine.title }}
      </p>
    </div>

    <!-- Form -->
    <form @submit.prevent="submitInquiry" class="space-y-4">
      <!-- Name Fields -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.firstName }} *
          </label>
          <input
            v-model="form.first_name"
            type="text"
            required
            class="input-field"
            :placeholder="translations.firstNamePlaceholder"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.lastName }} *
          </label>
          <input
            v-model="form.last_name"
            type="text"
            required
            class="input-field"
            :placeholder="translations.lastNamePlaceholder"
          />
        </div>
      </div>

      <!-- Email and Company -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.email }} *
          </label>
          <input
            v-model="form.email"
            type="email"
            required
            class="input-field"
            :placeholder="translations.emailPlaceholder"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.company }}
          </label>
          <input
            v-model="form.company"
            type="text"
            class="input-field"
            :placeholder="translations.companyPlaceholder"
          />
        </div>
      </div>

      <!-- Phone Field -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ translations.phone }}
        </label>
        <input
          v-model="form.phone"
          type="tel"
          class="input-field"
          :placeholder="translations.phonePlaceholder"
        />
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ translations.description }} *
        </label>
        <textarea
          v-model="form.message"
          required
          rows="4"
          class="input-field"
          :placeholder="translations.descriptionPlaceholder"
        ></textarea>
      </div>

      <!-- Actions -->
      <div class="flex space-x-3 pt-4">
        <button
          type="button"
          @click="$emit('close')"
          class="flex-1 btn-outline"
        >
          {{ translations.cancel }}
        </button>
        <button
          type="submit"
          :disabled="submitting"
          class="flex-1 btn-primary"
        >
          <span v-if="submitting" class="flex items-center justify-center">
            <svg
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ translations.sending }}
          </span>
          <span v-else>{{ translations.send }}</span>
        </button>
      </div>
    </form>
  </BaseModal>
</template>

<script setup>
import Swal from "sweetalert2";
import { computed, ref } from 'vue';
import BaseModal from "~/components/base/BaseModal.vue";

const props = defineProps({
  part: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["close"]);

const { $api } = useNuxtApp();
const { t } = useI18n();
const submitting = ref(false);

const translations = computed(() => {
  try {
    return {
      title: t('spareParts.inquiry.title'),
      partNumber: t('spareParts.inquiry.partNumber'),
      brand: t('spareParts.inquiry.brand'),
      compatibleWith: t('spareParts.inquiry.compatibleWith'),
      firstName: t('spareParts.inquiry.firstName'),
      lastName: t('spareParts.inquiry.lastName'),
      firstNamePlaceholder: t('spareParts.inquiry.firstNamePlaceholder'),
      lastNamePlaceholder: t('spareParts.inquiry.lastNamePlaceholder'),
      email: t('spareParts.inquiry.email'),
      emailPlaceholder: t('spareParts.inquiry.emailPlaceholder'),
      company: t('spareParts.inquiry.company'),
      companyPlaceholder: t('spareParts.inquiry.companyPlaceholder'),
      phone: t('spareParts.inquiry.phone'),
      phonePlaceholder: t('spareParts.inquiry.phonePlaceholder'),
      description: t('spareParts.inquiry.description'),
      descriptionPlaceholder: t('spareParts.inquiry.descriptionPlaceholder'),
      sending: t('spareParts.inquiry.sending'),
      send: t('spareParts.inquiry.send'),
      cancel: t('common.cancel'),
      successTitle: t('spareParts.inquiry.successTitle'),
      successMessage: t('spareParts.inquiry.successMessage'),
      successButton: t('spareParts.inquiry.successButton'),
      errorTitle: t('spareParts.inquiry.errorTitle'),
      errorMessage: t('spareParts.inquiry.errorMessage'),
      confirm: t('common.confirm')
    };
  } catch (error) {
    return {
      title: 'Spare Part Inquiry',
      partNumber: 'Part #',
      brand: 'Brand',
      compatibleWith: 'Compatible with',
      firstName: 'First Name',
      lastName: 'Last Name',
      firstNamePlaceholder: 'Your first name',
      lastNamePlaceholder: 'Your last name',
      email: 'Email',
      emailPlaceholder: 'your.email@example.com',
      company: 'Company',
      companyPlaceholder: 'Your company',
      phone: 'Phone',
      phonePlaceholder: 'Your phone number',
      description: 'Description',
      descriptionPlaceholder: 'Please describe your inquiry...',
      sending: 'Sending...',
      send: 'Send Inquiry',
      cancel: 'Cancel',
      successTitle: 'Inquiry Sent!',
      successMessage: 'Thank you for your inquiry. We will contact you soon.',
      successButton: 'OK',
      errorTitle: 'Error',
      errorMessage: 'Failed to send inquiry. Please try again.',
      confirm: 'Confirm'
    };
  }
});

const form = ref({
  first_name: "",
  last_name: "",
  email: "",
  company: "",
  phone: "",
  message: `Inquiry about spare part: ${props.part.name} (Part #${props.part.part_number})\n\nBrand: ${props.part.brand?.name || 'N/A'}\nCategory: ${props.part.category?.name || 'N/A'}\nStock: ${props.part.stock_quantity} units\n\n`
});

const submitInquiry = async () => {
  submitting.value = true;

      try {
      const requestData = {
        first_name: form.value.first_name,
        last_name: form.value.last_name,
        email: form.value.email,
        phone: form.value.phone,
        company: form.value.company,
        message: form.value.message,
        part_id: props.part.id
      };

      await $api.post("/contact/spare-part-inquiry", requestData);

    Swal.fire({
      icon: "success",
      title: translations.value.successTitle,
      text: translations.value.successMessage,
      confirmButtonText: translations.value.successButton,
      confirmButtonColor: "#10B981",
      timer: 3000,
      timerProgressBar: true,
    });

    emit("close");

    form.value = {
      first_name: "",
      last_name: "",
      email: "",
      company: "",
      phone: "",
      message: `Inquiry about spare part: ${props.part.name} (Part #${props.part.part_number})\n\nBrand: ${props.part.brand?.name || 'N/A'}\nCategory: ${props.part.category?.name || 'N/A'}\nStock: ${props.part.stock_quantity} units\n\n`
    };
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: translations.value.errorTitle,
      text: translations.value.errorMessage,
      confirmButtonText: translations.value.confirm,
      confirmButtonColor: "#EF4444",
    });
  } finally {
    submitting.value = false;
  }
};
</script>
